import { CALCULATION_TAG_NAME, DIFFICULTY_TAG_NAME } from '@exam-management/common/utils';
import { QuestionTagCategory, useGetTagsByCategoryQuery } from '@exam-management/graphql';
import {
  AddRoundedIcon,
  Button,
  Divider,
  Flex,
  Text,
  type OptionSelectionProps,
} from '@shared/components';
import { useMemo } from 'react';
import { useFieldArray, type FieldValues, type UseFormReturn } from 'react-hook-form';

import { ItemSetQuestionPropertiesSection } from '../../components';

function getOptionByTagName<
  T extends {
    name: string;
    id: string;
  },
>(
  tags: T[],
  tagName: string,
  options?: {
    fallbackLabel: string;
    fallbackValue: string;
  },
) {
  const tag = tags.find((t) => t.name === tagName);

  return {
    label: tag?.name ?? options?.fallbackLabel ?? tagName,
    value: tag?.id ?? options?.fallbackValue ?? tagName,
  };
}

export interface ItemSetQuestionConditionsSectionProps<
  TFields extends {
    itemSetType?: number;
    conditions: Array<unknown>;
  } = {
    itemSetType?: number;
    conditions: Array<unknown>;
  },
> {
  onApplyConditions: () => void;
  isLoading?: boolean;
  formContext: UseFormReturn<TFields>;
}

export default function ItemSetQuestionConditionsSection<TFields extends FieldValues>(
  props: Readonly<TFields>,
) {
  const { onApplyConditions, isLoading: isApplyingConditionsLoading, formContext } = props;

  const { fields, append, remove } = useFieldArray({
    name: 'conditions',
    control: formContext.control,
  });

  const { data: getDifficultyTagsResult, loading: gettingDifficultyTags } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Difficulty } },
    });

  const { data: getCalculationTagsResult, loading: gettingCalculationTags } =
    useGetTagsByCategoryQuery({
      variables: { input: { category: QuestionTagCategory.Calculation } },
    });

  const questionSize = formContext.watch('itemSetType') ?? 2;
  const difficultyOptions: OptionSelectionProps[] = useMemo(() => {
    const difficultyTags = getDifficultyTagsResult?.qbsGetTagsByCategory ?? [];

    return [
      getOptionByTagName(difficultyTags, DIFFICULTY_TAG_NAME.EASY, {
        fallbackLabel: 'ง่าย',
        fallbackValue: 'easy',
      }),
      getOptionByTagName(difficultyTags, DIFFICULTY_TAG_NAME.MEDIUM, {
        fallbackLabel: 'กลาง',
        fallbackValue: 'medium',
      }),
      getOptionByTagName(difficultyTags, DIFFICULTY_TAG_NAME.HARD, {
        fallbackLabel: 'ยาก',
        fallbackValue: 'hard',
      }),
    ];
  }, [getDifficultyTagsResult]);

  const calculationTypeOptions: OptionSelectionProps[] = useMemo(() => {
    const calculationTags = getCalculationTagsResult?.qbsGetTagsByCategory ?? [];

    return [
      getOptionByTagName(calculationTags, CALCULATION_TAG_NAME.CALCULATED, {
        fallbackLabel: 'คำนวณ',
        fallbackValue: 'calculated',
      }),
      getOptionByTagName(calculationTags, CALCULATION_TAG_NAME.NOT_CALCULATED, {
        fallbackLabel: 'ไม่คำนวณ',
        fallbackValue: 'non-calculated',
      }),
    ];
  }, [getCalculationTagsResult]);

  const handleAddCondition = () => {
    append({
      properties: Array.from({ length: questionSize }).map(() => ({
        difficulty: undefined,
        calculated: undefined,
      })),
      needTotal: undefined,
    });
  };

  const handleRemoveCondition = (index: number) => {
    remove(index);
  };

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        bgcolor: theme.palette.grey[50],
        borderRadius: theme.shape.rounded(2),
        border: '1px solid',
        borderColor: theme.palette.divider,
      })}
    >
      <Flex
        sx={(theme) => ({
          p: theme.spacing(3, 4),
        })}
      >
        <Text variant='body1Semibold'>Item Set Conditions</Text>
      </Flex>
      <Divider />
      <Flex
        sx={(theme) => ({
          p: theme.spacing(4),
          gap: theme.spacing(4),
          flexDirection: 'column',
        })}
      >
        {fields.map((field, order) => {
          return (
            <ItemSetQuestionPropertiesSection
              key={field.id}
              questionSize={questionSize}
              difficultyOptions={difficultyOptions}
              calculationTypeOptions={calculationTypeOptions}
              difficultyOptionsLoading={gettingDifficultyTags}
              calculationTypeOptionsLoading={gettingCalculationTags}
              prefixName={`conditions.[${order}]`}
              onClose={order !== 0 ? () => handleRemoveCondition(order) : undefined}
              errorMessage={formContext.formState.errors.conditions?.[order]?.message}
            />
          );
        })}
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Button
            variant='outlined'
            color='secondary'
            startIcon={<AddRoundedIcon />}
            onClick={handleAddCondition}
          >
            Add Condition
          </Button>
          <Button
            variant='outlined'
            onClick={onApplyConditions}
            loading={isApplyingConditionsLoading}
            disabled={!onApplyConditions}
          >
            Apply Condition
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
